/**
 * Types for the DCA allocation matrix.
 *
 * These mirror the contract on be-api-gateway-backoffice added by SO-32233:
 *   GET /api/v1/dca-allocation-config
 *   PUT /api/v1/dca-allocation-config?occupier=&accountState=&placementNumber=
 *
 * The table is keyed on (occupier, accountState, placementNumber) — there is no PSR
 * dimension. A PSR account is allocated by the same row as its non-PSR
 * equivalent, so the matrix has four rows rather than eight.
 */

/** Matches the Occupier proto enum on the gateway. */
export enum Occupier {
  Known = "KNOWN",
  Occupier = "OCCUPIER",
}

/** Matches the AccountState proto enum on the gateway. */
export enum AccountState {
  Live = "LIVE",
  Final = "FINAL",
}

/**
 * `placementNumber` throughout is the ordinal position of a debt placement - 1st,
 * 2nd, 3rd. Under the simplified config it is unambiguous: Moriarty no longer
 * consumes a placement number, so a rejected referral and an accepted-then-exhausted
 * one both continue to the same numbered placement. That is what makes
 * "placementNumber" the right name rather than a workflow step, and it is why there
 * is no 4th placement.
 */

/** One agency's share of a single (segment, step). */
export interface IDcaAllocation {
  dca: string
  percentage: number
}

/** A row as returned by GET. Each entry carries its own segment and step. */
export interface IDcaAllocationConfigEntry {
  occupier: Occupier
  accountState: AccountState
  placementNumber: number
  dca: string
  /**
   * 0 is meaningful: it keeps an agency retired-but-present so the screen can
   * display and re-enable it. GET deliberately does not filter these out.
   */
  percentage: number
}

/**
 * The body of PUT. Full-replace semantics for one (segment, step): submitted
 * agencies are upserted and any existing agency absent from the list is
 * deleted.
 *
 * The gateway validates, in order: each percentage within 0–100, no agency
 * listed twice, and a total of exactly 100.
 *
 * An empty array is the one exception and is the "retire this step entirely"
 * operation. Clearing a step therefore means sending `[]` — NOT a list of
 * agencies at 0, which would total 0 and be rejected.
 */
export interface IDcaAllocationUpdate {
  occupier: Occupier
  accountState: AccountState
  placementNumber: number
  allocations: IDcaAllocation[]
}

/** A single editable cell: the split for one segment at one step. */
export interface IDcaAllocationCell {
  occupier: Occupier
  accountState: AccountState
  placementNumber: number
  allocations: IDcaAllocation[]
}

/**
 * One agency's part in a single saved change, as returned by
 * GET /api/v1/dca-allocation-config/history.
 *
 * The config table is overwritten in place on every save, so this is the only record of
 * what a change replaced. Entries sharing a `changeId` are one save and cover the union
 * of the split before and after it, which is what lets a change be read on its own
 * rather than by replaying every change before it.
 *
 * Absent is not zero. A missing `previousPercentage` means the agency was not in the
 * split at all; 0 means it was there and held no volume, which is a real state - it is
 * how an agency stays retired-but-present so it can be re-enabled.
 */
export interface IDcaAllocationHistoryEntry {
  changeId: string
  dca: string
  previousPercentage: number | null
  newPercentage: number | null
  /** Absent for the seeded starting state, which no user made. */
  changedBy: string | null
  changedAt: string
}

/** The entries of one save, grouped for display. */
export interface IDcaAllocationChange {
  changeId: string
  changedBy: string | null
  changedAt: string
  entries: IDcaAllocationHistoryEntry[]
}
